import { EmployeeSignIns, EventData } from '../components/Shared/Types';
import { LOCATION_ICONS } from './constants';
import { isToday } from './date-helpers-consolidated';

const availableLocations = Object.keys(LOCATION_ICONS);


export function formatTime(date:Date) {
    // Ensure date is valid before formatting
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'Invalid Time'; // Or handle as appropriate
    }
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  export function formatDate(date:Date) {
    // Ensure date is valid before formatting
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'Invalid Date'; // Or handle as appropriate
    }
    return date.toLocaleDateString();
  }
  export function formatDuration(ms:number) {
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  const filterByDate = (signIn:EventData,date:Date) => {
    // Handle case where eventDate might be null
    if (!signIn.eventDate) return false;

    // Use eventDate for comparison
    const signInDate = signIn.eventDate;

    // Ensure comparison date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;

    return signInDate.getFullYear() === date.getFullYear() &&
           signInDate.getMonth() === date.getMonth() &&
           signInDate.getDate() === date.getDate();
  }

export function getSignInsForDate(employees:EmployeeSignIns[], date:Date) {
    const signIns:EmployeeSignIns[] = [];
    employees.forEach(emp => {

      if (emp.signIns === undefined) return; // Skip if no sign-ins
      if ( date === undefined) return; // Skip if no date

      const entries = emp.signIns.filter(e => filterByDate(e,date));
      if (entries.length) signIns.push({ ...emp, signIns: entries });
    });
    return signIns;
  }

  // Leaderboard filtering
export  function getVisitsCount(employee:EmployeeSignIns, month:number | null, year:number | null) {
    // If month or year is null, don't filter by that dimension
    return employee.signIns.filter(s => {
      if (s.eventType !== 'in' || s.isMainOffice) return false; // Count sign-ins to visited offices

      // Skip if no valid eventDate
      if (!s.eventDate) return false;

      if (month != null && s.eventDate.getMonth() !== month) return false;
      if (year != null && s.eventDate.getFullYear() !== year) return false;

      return true;
    }).length;
  }

  // Updated: Calculate total duration based on sign-out to sign-out cycle
export function getTotalDuration(employee:EmployeeSignIns, month:number | null, year:number | null) {
    let total = 0;
    const events = employee.signIns
      .filter(s => {
        // Skip if no eventDate
        if (!s.eventDate) return false;

        if (month != null && s.eventDate.getMonth() !== month) return false;
        if (year != null && s.eventDate.getFullYear() !== year) return false;
        return true;
      })
      .sort((a, b) => {
          return a.eventDate.getTime() - b.eventDate.getTime();
      });

    // Modified: Process from sign-out at main office to sign-out at visited office
    for (let i = 0; i < events.length - 2; i++) {
        const event1 = events[i];
        const event2 = events[i+1];
        const event3 = events[i+2];

        // Ensure dates are valid before calculation
        if (!event1.eventDate || !event3.eventDate) continue;

        // First event: out from main office
        if (event1.eventType === 'out' && event1.isMainOffice) {
          // Second event: in to visited office
          if (event2 && event2.eventType === 'in' && !event2.isMainOffice) {
            // Third event: out from visited office
            if (event3 && event3.eventType === 'out' && !event3.isMainOffice) {
              total += event3.eventDate.getTime() - event1.eventDate.getTime();
              i += 2; // Skip to the next set of events
            }
          }
        }
    }

    return total;
  }

  // Return sorted array for "Magni" only
export function getLeaderboardData(employees:EmployeeSignIns[], leaderboardMonth:number | null, leaderboardYear:number | null, category:string) {
    const data = employees
      .filter(emp => emp.type === "Magni")
      .map(emp => {
        if (category === 'visits') {
          return { ...emp, value: getVisitsCount(emp, leaderboardMonth, leaderboardYear) };
        } else {
          // Pass null for month/year if they are not applicable for 'duration' or handle appropriately
          return { ...emp, value: getTotalDuration(emp, leaderboardMonth, leaderboardYear) };
        }
      });
    // Filter out zero-value
    return data.filter(emp => emp.value > 0).sort((a, b) => b.value - a.value);
  }

  // Updated: For Magni Profile to handle modified trip cycles
export function getMagniProfileStats(user:EmployeeSignIns, dateFilter:string, customMonth:number | null, customYear:number | null) {
    // dateFilter can be 'week', 'month', 'year', 'all'
    // customMonth / customYear are numeric or null
    const now = new Date();
    const events = user.signIns.filter(e => {
      // Ensure there is at least one valid date
      if (!e.eventDate) return false;
      const eventDate = e.eventDate; // Use dateOut if dateIn is null

      // handle dateFilter
      if (dateFilter !== 'all') {
        if (dateFilter === 'year') {
          if (eventDate.getFullYear() !== now.getFullYear()) return false;
        } else if (dateFilter === 'month') {
          if (eventDate.getMonth() !== now.getMonth() || eventDate.getFullYear() !== now.getFullYear()) return false;
        } else if (dateFilter === 'week') {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          endOfWeek.setHours(23, 59, 59, 999);
          if (eventDate < startOfWeek || eventDate > endOfWeek) return false;
        }
      }
      // handle customMonth/customYear
      if (customMonth != null && eventDate.getMonth() !== customMonth) return false;
      if (customYear != null && eventDate.getFullYear() !== customYear) return false;
      return true;
    }).sort((a, b) => {
        // Sort using dateIn or dateOut, ensuring dates exist (filter should guarantee this)
        const dateA = a.eventDate;
        const dateB = b.eventDate;
        return dateA.getTime() - dateB.getTime();
    });

    // Count visits to locations (based on sign-ins to visited offices)
    const locationCounts:any = {};
    events.forEach((e:EventData) => {
      if (!e.isMainOffice && e.location) {
        locationCounts[e.location] = (locationCounts[e.location] || 0) + 1;
      }
    });
    let topLocations = Object.entries(locationCounts).map(([name, visits]) => ({ name, visits }));
    topLocations.sort((a:any, b:any) => b.visits - a.visits);
    topLocations = topLocations.slice(0, 5);

    // Group events into trips - modified to track from out at main to out at visited
    const timeOutOfOffice = [];
    let currentTrip:any = null;

    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const eventDate = event.eventDate; // Use dateOut if dateIn is null

      // Skip if eventDate is somehow still null (shouldn't happen due to filter)
      if (!eventDate) continue;



      currentTrip = {
        date: eventDate,
        startTime: eventDate, // Use the valid date
        fromLocation: event.location, //TODO: function to get from location
        events: [event],
        duration: 0// Calculate duration if dateIn is available,
      };
      
      timeOutOfOffice.push(currentTrip);
    }
      

    return { topLocations, timeOutOfOffice };
  }

  // For Guest Profile
export function getGuestProfileStats(user:EmployeeSignIns) {
    // Updated: Count visits based on sign-ins to visited offices
    const totalVisits = user.signIns.filter(e => e.eventType === "in" && !e.isMainOffice).length;
    const locationCounts:any = {};
    user.signIns.filter(e => e.eventType === "in" && !e.isMainOffice).forEach(e => {
      if(e.location) locationCounts[e.location] = (locationCounts[e.location] || 0) + 1;
    });
    return { totalVisits, locationBreakdown: locationCounts };
  }

  // For Locations View - get visitors for a specific location
export function getVisitorsForLocation(employees:EmployeeSignIns[], location:string, timeFilter:string, customDate: Date | null = null) {
    const now = new Date();
    let filterStartDate: Date;
    let filterEndDate: Date;

    // Determine date range based on filter
    if (timeFilter === 'day') {
        const baseDate = customDate instanceof Date && !isNaN(customDate.getTime()) ? customDate : now;
        filterStartDate = new Date(baseDate);
        filterStartDate.setHours(0, 0, 0, 0);
        filterEndDate = new Date(baseDate);
        filterEndDate.setHours(23, 59, 59, 999);
    } else {
        // For week, month, year - calculate start date
        filterStartDate = new Date(now);
        if (timeFilter === 'week') {
            filterStartDate.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
            filterStartDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === 'month') {
            filterStartDate.setDate(1); // Start of month
            filterStartDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === 'year') {
            filterStartDate.setMonth(0, 1); // Start of year
            filterStartDate.setHours(0, 0, 0, 0);
        }
        // End date is always 'now' for these filters
        filterEndDate = new Date(now);
    }

    const visitors:EmployeeSignIns[] = [];
    employees.forEach(emp => {
      const visits = emp.signIns.filter((s:EventData) => {
        // Basic filtering
        if (s.location !== location ||  s.isMainOffice) {
            return false;
        }

        // Use dateIn or dateOut for time comparison
        const eventDate = s.eventDate;
        if (!eventDate) return false; // Skip if no valid date

        // Check if eventDate falls within the calculated range
        return eventDate >= filterStartDate && eventDate <= filterEndDate;
      });

      if (visits.length > 0) {
        // Sort visits chronologically using the valid date
        const sortedVisits = [...visits].sort((a, b) => {
            const dateA = a.eventDate;
            const dateB = b.eventDate;
            return dateA.getTime() - dateB.getTime();
        });

        visitors.push({
          ...emp,
          signIns: sortedVisits
        });
      }
    });

    return visitors;
  }


  // Get location visit counts for the specified time period
export function getLocationVisitCounts(employees:EmployeeSignIns[], timeFilter:string) {
    const now = new Date();
    let startDate = new Date(now);

    // Set time range based on filter
    if (timeFilter === 'day') {
      startDate.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'week') {
      startDate.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      startDate.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'month') {
      startDate.setDate(1); // Start of month
      startDate.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'year') {
      startDate.setMonth(0, 1); // Start of year
      startDate.setHours(0, 0, 0, 0);
    }

    const endDate = new Date(now); // End date is always 'now'
    const locationCounts:any = {};

    // Initialize all locations with 0 count
    availableLocations.forEach(loc => {
      locationCounts[loc] = 0;
    });

    // Count visits to each location
    employees.forEach((emp:EmployeeSignIns) => {
      emp.signIns.forEach((s:EventData) => {
        // Use dateIn or dateOut for time comparison
        const eventDate = s.eventDate;
        
        const isValidLocation:boolean = availableLocations.includes(s.location);
        const dateCheck:boolean = eventDate && eventDate.getTime() >= startDate.getTime() && eventDate.getTime() <= endDate.getTime();
        // Perform checks only if eventDate is valid
        if (eventDate && dateCheck && !s.isMainOffice && s.location && isValidLocation // Ensure location is valid
           ) {
          locationCounts[s.location] = (locationCounts[s.location] || 0) + 1;
        }
      });
    });

    return locationCounts;
  }

  export const calculateTotalTimeAway = (signIns:EventData[]) => {
    let totalTime = 0;
    const todayEvents = signIns.filter((event:EventData) => isToday(event.eventDate));
    
    if (todayEvents.length < 2) return 0;
    
    // Sort events chronologically
    const sortedEvents = [...todayEvents].sort((a:EventData, b:EventData) => 
      a.eventDate.getTime() - b.eventDate.getTime()
    );
    
    // Handle single in and out event case
    if (sortedEvents.length === 2) {
      const firstEvent = sortedEvents[0];
      const secondEvent = sortedEvents[1];
      
      if (firstEvent.eventType === 'out' && secondEvent.eventType === 'in') {
        return secondEvent.eventDate.getTime() - firstEvent.eventDate.getTime();
      }
    }
    
    // Calculate total travel time (time between signing out of one office and signing in to another)
    for (let i = 0; i < sortedEvents.length - 1; i++) {
      const currentEvent = sortedEvents[i];
      const nextEvent = sortedEvents[i+1];
      
      // If we find an "out" event followed by an "in" event, that's travel time
      if (currentEvent.eventType === 'out' && nextEvent.eventType === 'in') {
      // Calculate the time difference between signing out and signing in
      const travelTime = nextEvent.eventDate.getTime() - currentEvent.eventDate.getTime();
      totalTime += travelTime;
      }
    }
    
    return totalTime;
  };


  export const IsTabletAccount = ():boolean => {
    const user = localStorage.getItem('user');
    if (user) {
      const parsedUser = JSON.parse(user);
      return parsedUser.accountType === 'tablet';
    }
    return false;
  }